import type { Invoice } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

import { BALANCE_RETURNED_PART } from "../constants/balance-returned";
import type { BalanceReturned } from "../types/balance-returned";

// Pure and free of React, Medplum-client and statement-status imports, so the statement-generation
// bot can bundle it and `statement-status` can depend on it without a cycle.

/**
 * Reads the balance-returned record off a statement.
 *
 * Every part is written together, so a record missing any of them, or holding a value of the wrong
 * type, reads as absent.
 *
 * @param invoice The statement being read.
 * @returns The record, or `undefined` when the statement was never cancelled into its parent.
 */
export function readBalanceReturned(invoice: Invoice): BalanceReturned | undefined {
  const parent = invoice.extension?.find(
    (extension) => extension.url === Extensions.BalanceReturned,
  );
  const part = (url: string) => parent?.extension?.find((entry) => entry.url === url);

  const amount = part(BALANCE_RETURNED_PART.Amount)?.valueMoney?.value;
  const toStatement = part(BALANCE_RETURNED_PART.ToStatement)?.valueString;
  const returnedAt = part(BALANCE_RETURNED_PART.ReturnedAt)?.valueDateTime;
  if (typeof amount !== "number" || !toStatement || !returnedAt) return undefined;

  return { amount, toStatement, returnedAt };
}

/**
 * Whether this is a cancelled sub-statement: `status: cancelled` and a complete balance-returned
 * record. A partial or malformed record reads as not cancelled.
 *
 * @param invoice The statement being read.
 * @returns True for a cancelled sub-statement.
 */
export function isCancelledSubStatement(invoice: Invoice): boolean {
  return invoice.status === "cancelled" && readBalanceReturned(invoice) !== undefined;
}
